<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">{{ typeLabel }}</h1>
          <p class="text-sm text-gray-400 mt-1">
            <template v-if="contentType === 'NOTA'">
              Gestioná las notas destacadas de la Federación
            </template>
            <template v-else>
              Gestioná las historias, anécdotas y notas de la Federación
            </template>
          </p>
        </div>
        <div class="flex w-full gap-2 sm:w-auto">
          <router-link :to="newPath" class="btn-bochas-rojo w-full text-center text-sm sm:w-auto">
            <template v-if="contentType === 'NOTA'">+ Nueva nota</template>
            <template v-else>+ Nueva historia</template>
          </router-link>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4">
        {{ error }}
      </div>

      <!-- Empty -->
      <div v-else-if="items.length === 0" class="text-center py-12">
        <p class="text-gray-400 mb-4">{{ emptyLabel }}</p>
        <router-link :to="newPath" class="btn-bochas-rojo inline-block">
          {{ firstLabel }}
        </router-link>
      </div>

      <!-- Tabla desktop / Cards mobile -->
      <div v-else>
        <div class="hidden md:block card-fbochas overflow-hidden">
          <table class="w-full">
            <thead class="bg-white/5 border-b border-white/10">
              <tr class="text-left text-xs uppercase tracking-wide text-gray-400">
                <th class="px-4 py-3">Título</th>
                <th class="px-4 py-3">Tipo</th>
                <th class="px-4 py-3">Fecha</th>
                <th class="px-4 py-3">Estado</th>
                <th v-if="canViewRecordHistory" class="px-4 py-3">Registro</th>
                <th class="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              <tr v-for="h in items" :key="h.id" class="hover:bg-white/5 transition-colors">
                <td class="px-4 py-3 text-white font-medium">{{ h.title }}</td>
                <td class="px-4 py-3">
                  <span :class="[
                    'text-xs px-2 py-1 rounded font-semibold uppercase',
                    h.type === 'HISTORIA'
                      ? 'bg-bochas-azul/20 text-bochas-azul'
                      : 'bg-bochas-rojo/20 text-bochas-rojo',
                  ]">
                    {{ h.type === 'HISTORIA' ? 'Historia' : 'Nota' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ formatDate(h.published_at) }}</td>
                <td class="px-4 py-3">
                  <span :class="[
                    'text-xs px-2 py-1 rounded',
                    h.status === 'published'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-yellow-500/20 text-yellow-300',
                  ]">
                    {{ h.status === 'published' ? 'Publicado' : 'Borrador' }}
                  </span>
                </td>
                <td v-if="canViewRecordHistory" class="px-4 py-3"><AdminRecordDates v-if="canViewRecordHistory" resource="news" :record="h" /></td>
                <td class="px-4 py-3 text-right">
                  <div class="inline-flex items-center justify-end gap-2">
                    <router-link
                      :to="editPath(h.id)"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                      title="Editar"
                      :aria-label="`Editar ${contentType === 'NOTA' ? 'nota' : 'historia'}`"
                    ><IconFbochas name="edit" :size="16" /></router-link>
                    <button
                      @click="onDelete(h)"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                      title="Eliminar"
                      :aria-label="`Eliminar ${contentType === 'NOTA' ? 'nota' : 'historia'}`"
                    ><IconFbochas name="trash" :size="16" /></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden space-y-3">
          <div v-for="h in items" :key="h.id" class="card-fbochas p-4">
            <div class="flex items-start justify-between gap-2 mb-2">
              <h3 class="text-white font-semibold flex-1">{{ h.title }}</h3>
              <span :class="[
                'text-xs px-2 py-1 rounded font-semibold uppercase flex-shrink-0',
                h.type === 'HISTORIA'
                  ? 'bg-bochas-azul/20 text-bochas-azul'
                  : 'bg-bochas-rojo/20 text-bochas-rojo',
              ]">
                {{ h.type === 'HISTORIA' ? 'Historia' : 'Nota' }}
              </span>
            </div>
            <p class="text-xs text-gray-400 mb-3">📅 {{ formatDate(h.published_at) }}</p>
            <AdminRecordDates v-if="canViewRecordHistory" resource="news" :record="h" class="mb-3" />
            <div class="flex items-center justify-end gap-2">
              <router-link
                :to="editPath(h.id)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
               title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
              <button
                @click="onDelete(h)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
               title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getStoredUser } from "@/services/auth/authService";
import { isGeneralAdmin } from "@/utils/roles";

import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import AdminRecordDates from "@/components/Admin/AdminRecordDates.vue";
import { listarNoticiasAdmin, eliminarNoticia } from "@/services/noticias/noticiasService";
import { formatDate } from "@/utils/date";
import { useConfirm } from "@/composables/useConfirm";

const canViewRecordHistory = computed(() => isGeneralAdmin(getStoredUser()));

const route = useRoute();
const { confirm } = useConfirm();

// Está vista reusa el mismo componente para 2 contentType distintos: HISTORIA y NOTA.
// El type lo define la ruta via meta.contentType (router/index.js).
const contentType = route.meta?.contentType || "HISTORIA";

const typeLabel = computed(() =>
  contentType === "NOTA" ? "Notas" : "Historias y Notas",
);
const listPath = computed(() =>
  contentType === "NOTA" ? "/admin/notas" : "/admin/historias",
);
const newPath = computed(() =>
  contentType === "NOTA" ? "/admin/notas/nueva" : "/admin/historias/nueva",
);
const editPath = (id) =>
  contentType === "NOTA" ? `/admin/notas/editar/${id}` : `/admin/historias/editar/${id}`;
const emptyLabel = computed(() =>
  contentType === "NOTA" ? "Aún no hay notas publicadas." : "Aún no hay historias ni notas publicadas.",
);
const firstLabel = computed(() =>
  contentType === "NOTA" ? "Crear la primera nota" : "Crear la primera",
);

const items = ref([]);
const loading = ref(true);
const error = ref("");

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarNoticiasAdmin();
    // Filtrar solo por el type de la ruta
    items.value = (data.items || []).filter((n) => n.type === contentType);
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "Error al cargar.";
  } finally {
    loading.value = false;
  }
}

async function onDelete(h) {
  const confirmed = await confirm({
    title: "Eliminar",
    message: `¿Eliminar "${h.title}"?`,
  });
  if (!confirmed) return;
  try {
    await eliminarNoticia(h.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo eliminar.";
  }
}

onMounted(load);
</script>
